import { Component, ElementRef, HostListener, OnDestroy } from "@angular/core";
import { SessionApi } from "../../api/session.api";
import { IAuthCustomerUser } from "../../model/customer.user.model";
import { filter, Subject, switchMap, take, takeUntil, timeout } from "rxjs";
import { logger } from "../../util/Logger";
import { AuthService } from "../../services/auth.service";
import { NotificationsService } from "angular2-notifications";

const className = "SwitchCompanyComponent";

@Component({
  selector: "app-switch-company",
  templateUrl: "./switch-company.component.html",
  styleUrls: ["./switch-company.component.sass"],
})
export class SwitchCompanyComponent implements OnDestroy {
  companies: IAuthCustomerUser[] = [];
  isSwitch: boolean = false;

  private unsubscribe$ = new Subject<void>();
  isAdmin: boolean = false;
  company: string;
  currentUser: number | null;
  allCompanies: IAuthCustomerUser[] = [];

  isFocused: boolean = false;
  searchText: string = "";
  constructor(
    private session: SessionApi,
    private authService: AuthService,
    private notifications: NotificationsService,
    private elementRef: ElementRef,
  ) {
    this.company = session.getCustomerUser()?.customer?.name || "Company";
    this.currentUser = session.getCustomerUser()?.id || null;

    session.$userData.subscribe((userData) => {
      if (userData) {
        this.isAdmin = userData.isAdmin || false;
        this.allCompanies = userData.customerUsers || [];
        this.companies = this.sortCompanies(this.allCompanies);
      } else {
        this.companies = [];
        this.allCompanies = [];
        this.isAdmin = false;
      }
    });
  }

  public switchCustomer(customerUser: IAuthCustomerUser) {   
    if (customerUser.id === this.currentUser) {
      return;
    } 
    
    const signature =
      className + `.switchCustomer: CustomerId[${customerUser.customerId}]`;
    const currentUser = this.session.$userData.getValue();

    if (!currentUser) {
      logger.error(signature + `Unauthenticated user cannot switch customers`);
      return;
    }

    if (customerUser.customerId) {
      this.isSwitch = true;
      this.authService
        .switchUser(customerUser.customerId, currentUser.id)
        .pipe(
          switchMap(() =>
            this.session.$customerUser.pipe(
              filter(
                (
                  activeCustomerUser,
                ): activeCustomerUser is IAuthCustomerUser =>
                  !!activeCustomerUser &&
                  activeCustomerUser.customerId === customerUser.customerId,
              ),
              take(1),
              timeout({ first: 15000 }),
            ),
          ),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(
          (activeCustomerUser) => {
            this.currentUser = activeCustomerUser.id;
            this.company = activeCustomerUser.customer?.name || "Company";
            this.searchText = "";
            this.companies = this.sortCompanies(this.allCompanies);
            this.notifications.success("Switch", "Your access was updated");
            this.isSwitch = false;
          },
          (err) => {
            this.isSwitch = false;
            if (err.error && err.error.statusCode == 400) {
              this.notifications.error(
                "Switch",
                "You are not authorized to switch this customer user",
              );
              return;
            }
            this.notifications.error("Switch", "Something wrong");
          },
        );
    }
  }

  searchCompany(event: Event) {
    const input = (event.target as HTMLInputElement).value.toLowerCase();

    this.searchText = input;

    this.companies = this.sortCompanies(
      this.allCompanies.filter((company) =>
        company.customer?.name.toLowerCase().includes(input),
      ),
    );
  }

  resetSearch() {
    this.searchText = "";
    this.isFocused = false;
    this.companies = this.sortCompanies(this.allCompanies);
  }

  @HostListener("document:click", ["$event"])
  onClickOutside(event: Event) {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);

    if (!clickedInside) {
      this.resetSearch();
    }
  }

  private sortCompanies(companies: IAuthCustomerUser[]): IAuthCustomerUser[] {
    return [...companies].sort((a, b) => {
      if (a.id === this.currentUser) return -1;
      if (b.id === this.currentUser) return 1;
      return 0;
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
